import React from 'react';
import { useSelector } from 'react-redux';
import {
  AppContent,
  AuthorPremium,
  ErrorBoundary,
  SecondarySectionTitle,
  SectionHeader,
  SectionWithTitle,
  ThreeVCExpandable,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getArticles,
  getBio,
  getByline,
  getImage,
  getName,
  getSectionTitle,
  getShowMorePaginationText,
  getShowMorePaginationURLByKey,
  getSocialButtons,
} from '../../store/template/authorPage/authorPageAbuDhabi/authorPageAbuDhabi.selectors';
import { AuthorPageSectionName } from '../../store/template/authorPage/authorPage.reducer';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { DefaultNavigationLayout } from '../../components/connectedComponents/DefaultNavigationLayout';

interface AuthorPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const AuthorPageAbuDhabiComponent: React.FunctionComponent<AuthorPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <AuthorPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    justifyItems: 'center',
  },
});

export const AuthorPageContentConnected: React.FunctionComponent = () => {
  const articles = useSelector(getArticles);
  const sectionTitle = useSelector(getSectionTitle);
  const image = useSelector(getImage);
  const name = useSelector(getName);
  const byline = useSelector(getByline);
  const socialButtons = useSelector(getSocialButtons);
  const bio = useSelector(getBio);
  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags articles={articles} description={bio} title={name} pageImage={null} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <DefaultNavigationLayout mainWrapperConfigThemeKey="mainContent">
            <div className={css(style.sectionsWrapper)}>
              <AuthorPremium image={image} name={name} socialButtons={socialButtons} byline={byline} />

              <SectionWithTitle>
                <SectionHeader
                  moreButtonSvg={null}
                  moreButtonText={null}
                  paddingFactors={{ large: 1, medium: 0.5, small: 1 }}
                >
                  <SecondarySectionTitle>{sectionTitle}</SecondarySectionTitle>
                </SectionHeader>
                <ThreeVCExpandable cards={articles} paddingFactors={{ large: 1, medium: 0.5, small: 1 }} />
              </SectionWithTitle>
            </div>

            <LoadMoreAuthorPostsButtonConnected />

          </DefaultNavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};


export const LoadMoreAuthorPostsButtonConnected: React.FunctionComponent = () => {
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(AuthorPageSectionName));
  const text = useSelector(getShowMorePaginationText);
  const onClick = useFetchNextPosts(AuthorPageSectionName, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
